import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { format, subDays } from 'date-fns'
import type { Site } from '@/types/database'

interface SiteWithStats {
  id: string
  domain: string
  name: string
  user_id: string
  user_email: string
  sessions_30d: number
  events_30d: number
  events_today: number
  created_at: string
}

export default function AdminSitesPage() {
  const [sites, setSites] = useState<SiteWithStats[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<'events' | 'sessions' | 'created'>('events')

  useEffect(() => {
    fetchSites()
  }, [sortBy])

  const fetchSites = async () => {
    setIsLoading(true)

    try {
      // Get all sites with user emails
      const { data: sitesData } = await supabase
        .from('sites')
        .select('*, profiles!inner(email)') as { data: (Site & { profiles: { email: string } })[] | null }

      if (!sitesData) {
        setSites([])
        return
      }

      const thirtyDaysAgo = subDays(new Date(), 30).toISOString()
      const todayStart = new Date()
      todayStart.setHours(0, 0, 0, 0)

      // Get stats for each site
      const sitesWithStats = await Promise.all(
        sitesData.map(async (site) => {
          const { count: sessions30d } = await supabase
            .from('sessions')
            .select('*', { count: 'exact', head: true })
            .eq('site_id', site.id)
            .gte('started_at', thirtyDaysAgo)

          const { count: events30d } = await supabase
            .from('events')
            .select('*', { count: 'exact', head: true })
            .eq('site_id', site.id)
            .gte('timestamp', thirtyDaysAgo)

          const { count: eventsToday } = await supabase
            .from('events')
            .select('*', { count: 'exact', head: true })
            .eq('site_id', site.id)
            .gte('timestamp', todayStart.toISOString())

          return {
            id: site.id,
            domain: site.domain,
            name: site.name,
            user_id: site.user_id,
            user_email: (site.profiles as { email: string }).email,
            sessions_30d: sessions30d || 0,
            events_30d: events30d || 0,
            events_today: eventsToday || 0,
            created_at: site.created_at,
          }
        })
      )

      // Sort
      sitesWithStats.sort((a, b) => {
        if (sortBy === 'events') return b.events_30d - a.events_30d
        if (sortBy === 'sessions') return b.sessions_30d - a.sessions_30d
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      })

      setSites(sitesWithStats)
    } catch (error) {
      console.error('Failed to fetch sites:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const filteredSites = sites.filter(
    (site) =>
      site.domain.toLowerCase().includes(search.toLowerCase()) ||
      site.user_email.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Site Monitor</h1>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 flex flex-wrap gap-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by domain or email..."
          className="input bg-gray-700 border-gray-600 text-white placeholder-gray-400"
        />

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          className="input bg-gray-700 border-gray-600 text-white"
        >
          <option value="events">Sort by Events</option>
          <option value="sessions">Sort by Sessions</option>
          <option value="created">Sort by Created</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white" />
        </div>
      ) : (
        <div className="bg-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="px-4 py-3 font-medium">Domain</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium text-right">Sessions (30d)</th>
                <th className="px-4 py-3 font-medium text-right">Events (30d)</th>
                <th className="px-4 py-3 font-medium text-right">Events Today</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredSites.map((site) => (
                <tr key={site.id} className="border-b border-gray-700 hover:bg-gray-700/50">
                  <td className="px-4 py-3 text-white font-medium">{site.domain}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/admin/users/${site.user_id}`}
                      className="text-primary-400 hover:text-primary-300"
                    >
                      {site.user_email}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-right">
                    {site.sessions_30d.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-right">
                    {site.events_30d.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-300 text-right">
                    {site.events_today.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-gray-400">
                    {format(new Date(site.created_at), 'MMM d, yyyy')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredSites.length === 0 && (
            <p className="text-center text-gray-400 py-8">No sites found</p>
          )}
        </div>
      )}
    </div>
  )
}
